
import Page from '@/Page.vue';
import { Component } from 'vue-property-decorator';
import moment from 'moment-timezone';

// Timezone pro zobrazování - vždy Praha bez ohledu na TZ prohlížeče
const DISPLAY_TIMEZONE = 'Europe/Prague';
import { authStore } from '@/libs/auth/+store/store';
import { AuthGetter } from '@/libs/auth/models/auth-state';
import TpmFiltersBar from '@/libs/tpm-analytics/components/TpmFiltersBar.vue';
import MissingCycleTimesAlert from '@/libs/tpm-analytics/components/MissingCycleTimesAlert.vue';
import { getDateRange } from '@/libs/tpm-analytics/core/date-range.utils';
import { GlobalVariables } from '@/libs/core/global';
import { tenantsStore } from '@/libs/tenants/+state/store';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { getFilterById, unpackSettings } from '@/libs/tpm-analytics/core/saved-filters';
import { getOptimalWindowSize } from '@/libs/tpm-analytics/core/chart-zoom-storage';
import { getDayStartMinutes } from '@/libs/tpm-analytics/core/production-day.utils';
import {
    getAllPlanFulfillmentAnalytics,
} from '@/libs/tpm-analytics/services/plan-fulfillment.service';
import {
    PlanFulfillmentSummary,
    PlanFulfillmentTrendPoint,
    PlanFulfillmentByWorkplace,
    PlanFulfillmentTrendByWorkplace,
    PlanFulfillmentByProject,
    PlanFulfillmentByShiftTeam,
    PlanFulfillmentByShiftType,
    PlanFulfillmentDistribution,
    PlanFulfillmentTopItem,
} from '@/libs/tpm-analytics/core/types';

@Component({ components: { TpmFiltersBar, MissingCycleTimesAlert } })
export default class TpmAnalyticsPlanFulfillment extends Page {
    // Loading state
    loading = false;
    filtersDebounceTimer: any = null;
    applyingInitial = false;
    
    fulfillmentPct: number | null = null;
    avgDailyFulfillmentPct: number | null = null;
    daysUnderPlan = 0;
    totalDays = 0;
    totalPieceDiff = 0;
    prevFulfillmentPct: number | null = null;
    prevAvgDailyFulfillmentPct: number | null = null;
    
    trend: PlanFulfillmentTrendPoint[] = [];
    workplaceData: PlanFulfillmentByWorkplace[] = [];
    workplaceTrendData: PlanFulfillmentTrendByWorkplace[] = [];
    workplaceChartMode: 'trend' | 'average' = 'trend';
    projectData: PlanFulfillmentByProject[] = [];
    teamData: PlanFulfillmentByShiftTeam[] = [];
    typeData: PlanFulfillmentByShiftType[] = [];
    distributionData: PlanFulfillmentDistribution | null = null;
    bestItems: PlanFulfillmentTopItem[] = [];
    worstItems: PlanFulfillmentTopItem[] = [];
    
    viewStart: number | null = null;
    viewEnd: number | null = null;
    wpViewStart: number | null = null;
    wpViewEnd: number | null = null;
    projViewStart: number | null = null;
    projViewEnd: number | null = null;
    teamViewStart: number | null = null;
    teamViewEnd: number | null = null;
    typeViewStart: number | null = null;
    typeViewEnd: number | null = null;
    
    granularity: 'day' | 'week' | 'month' = 'day';
    
    // Filter state
    periodPreset = 'last7d';
    customFrom: string | null = null;
    customTo: string | null = null;
    selectedLocationIds: string[] = [];
    selectedWorkplaceIds: string[] = [];
    selectedProjectIds: string[] = [];
    selectedShiftTypeIds: string[] = [];
    selectedShiftTeamTypeIds: string[] = [];

    get tenantId(): string | null {
        return authStore.useGetter(AuthGetter.GetTenant);
    }

    get daysUnderPlanPct(): number {
        if (this.totalDays === 0) return 0;
        return Math.round((this.daysUnderPlan / this.totalDays) * 100);
    }

    get deltaFulfillmentPct(): number {
        if (this.prevFulfillmentPct == null || this.prevFulfillmentPct === 0) return 0;
        return ((this.fulfillmentPct ?? 0) - this.prevFulfillmentPct) / this.prevFulfillmentPct * 100;
    }

    get deltaAvgDailyPct(): number {
        if (this.prevAvgDailyFulfillmentPct == null || this.prevAvgDailyFulfillmentPct === 0) return 0;
        return ((this.avgDailyFulfillmentPct ?? 0) - this.prevAvgDailyFulfillmentPct) / this.prevAvgDailyFulfillmentPct * 100;
    }

    get trendSeries(): any[] {
        try {
            if (!this.trend || this.trend.length === 0) {
                console.log('[PlanFulfillment] trendSeries: No trend data available');
                return [];
            }
            
        const filtered = this.getVisibleData(this.trend, this.viewStart, this.viewEnd);
            
            if (!filtered || filtered.length === 0) {
                console.log('[PlanFulfillment] trendSeries: No filtered data after zoom');
                return [];
            }
            
            const series = [
            {
                name: this.$t('tpmAnalytics.fulfillmentPct'),
                data: filtered.map((p) => p.fulfillmentPct ?? 0),
                type: 'line',
            },
            {
                name: this.$t('tpmAnalytics.pieceDiff'),
                data: filtered.map((p) => p.pieceDiff),
                type: 'column',
                yAxisIndex: 1,
            },
        ];
            
            console.log('[PlanFulfillment] trendSeries generated:', {
                dataPoints: filtered.length,
                seriesCount: series.length,
                firstPoint: filtered[0],
            });
            
            return series;
        } catch (error) {
            console.error('[PlanFulfillment] Error in trendSeries:', error, {
                trendLength: this.trend?.length,
                viewStart: this.viewStart,
                viewEnd: this.viewEnd,
            });
            return [];
        }
    }

    get trendOptions(): any {
        try {
        const self = this;
        const dayStartMinutes = getDayStartMinutes();
        const dayStartMs = dayStartMinutes * 60 * 1000;
            
            if (!this.trend || this.trend.length === 0) {
                console.log('[PlanFulfillment] trendOptions: No trend data, returning default options');
                return {
                    chart: { type: 'line', toolbar: { show: false }, animations: { enabled: false }, zoom: { enabled: false } },
                    xaxis: { categories: [] },
                    yaxis: [
                        { title: { text: String(this.$t('tpmAnalytics.fulfillmentPercent')) } },
                        { opposite: true, title: { text: String(this.$t('tpmAnalytics.pieceDifference')) } },
                    ],
                };
            }
        
        const categories = this.trend.map((d) => {
            let timestamp: number;
            
            if (this.granularity === 'week') {
                timestamp = moment(d.day).startOf('isoWeek').valueOf() + dayStartMs;
            } else if (this.granularity === 'month') {
                timestamp = moment(d.day).startOf('month').valueOf() + dayStartMs;
            } else {
                timestamp = moment(d.day).startOf('day').valueOf() + dayStartMs;
            }
            
            return self.formatAxisLabel(timestamp, self.granularity);
        });
            
            console.log('[PlanFulfillment] trendOptions generated:', {
                categoriesCount: categories.length,
                granularity: this.granularity,
        });
        
        return {
            chart: { type: 'line', toolbar: { show: false }, animations: { enabled: false }, zoom: { enabled: false } },
            stroke: { width: [2, 0], curve: 'smooth' },
            colors: ['#10b981', '#ef4444'],
            xaxis: {
                type: 'category',
                categories: categories,
                labels: {
                    rotate: self.granularity === 'week' ? -45 : 0,
                    formatter(value: any) {
                        if (typeof value === 'string' && value.includes('|')) {
                            const parts = value.split('|');
                            return parts;
                        }
                        return value;
                    },
                },
            },
            yaxis: [
                {
                    title: { text: String(self.$t('tpmAnalytics.fulfillmentPercent')) },
                    labels: { formatter: (val: number) => val ? val.toFixed(0) + '%' : '0%' },
                },
                {
                    opposite: true,
                    title: { text: String(self.$t('tpmAnalytics.pieceDifference')) },
                    labels: { formatter: (val: number) => Math.round(val).toLocaleString() },
                },
            ],
            legend: { position: 'top' },
            tooltip: { 
                shared: true, 
                intersect: false, 
                followCursor: true,
                    custom: function({ series, seriesIndex, dataPointIndex, w }: any) {
                        if (dataPointIndex < 0 || dataPointIndex >= self.trend.length) {
                            return '<div></div>';
                        }
                        
                        const d = self.trend[dataPointIndex];
                            let timestamp: number;
                            
                            if (self.granularity === 'week') {
                                timestamp = moment(d.day).startOf('isoWeek').valueOf() + dayStartMs;
                            } else if (self.granularity === 'month') {
                                timestamp = moment(d.day).startOf('month').valueOf() + dayStartMs;
                            } else {
                                timestamp = moment(d.day).startOf('day').valueOf() + dayStartMs;
                            }
                            
                        const dateLabel = self.formatAxisLabel(timestamp, self.granularity, true);
                        const fulfillmentPct = d.fulfillmentPct ?? 0;
                        const pieceDiff = d.pieceDiff ?? 0;
                        const okCount = d.okCount ?? 0;
                        const planCount = d.planCount ?? 0;
                        
                        return `
                            <div style="background: white; padding: 12px; border-radius: 6px; box-shadow: 0 2px 8px rgba(0,0,0,0.15); min-width: 200px;">
                                <div style="font-weight: 600; margin-bottom: 10px; padding-bottom: 8px; border-bottom: 1px solid #e5e7eb; color: #111827;">
                                    ${dateLabel}
                                </div>
                                <div style="display: flex; align-items: center; margin-bottom: 6px;">
                                    <span style="width: 12px; height: 12px; background: #10b981; border-radius: 2px; margin-right: 8px;"></span>
                                    <span style="color: #6b7280; font-size: 13px; flex: 1;">${self.$t('tpmAnalytics.fulfillmentPercent')}</span>
                                    <span style="font-weight: 600; color: #111827; font-size: 14px;">${fulfillmentPct.toFixed(1)}%</span>
                                </div>
                                <div style="display: flex; align-items: center; margin-bottom: 6px;">
                                    <span style="width: 12px; height: 12px; background: #ef4444; border-radius: 2px; margin-right: 8px;"></span>
                                    <span style="color: #6b7280; font-size: 13px; flex: 1;">${self.$t('tpmAnalytics.pieceDifference')}</span>
                                    <span style="font-weight: 600; color: #111827; font-size: 14px;">${Math.round(pieceDiff).toLocaleString()}</span>
                                </div>
                                <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #f3f4f6;">
                                    <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                                        <span style="color: #6b7280; font-size: 12px;">${self.$t('tpmAnalytics.produced')}</span>
                                        <span style="font-weight: 600; color: #111827; font-size: 13px;">${Math.round(okCount).toLocaleString()}</span>
                                    </div>
                                    <div style="display: flex; justify-content: space-between;">
                                        <span style="color: #6b7280; font-size: 12px;">${self.$t('tpmAnalytics.plan')}</span>
                                        <span style="font-weight: 600; color: #111827; font-size: 13px;">${Math.round(planCount).toLocaleString()}</span>
                                    </div>
                                </div>
                            </div>
                        `;
                    },
            },
            annotations: {
                yaxis: [{
                    y: 100,
                    borderColor: '#10b981',
                    strokeDashArray: 4,
                    label: { text: '100%', style: { color: '#fff', background: '#10b981' } },
                }],
            },
        };
        } catch (error) {
            console.error('[PlanFulfillment] Error in trendOptions:', error);
            return {
                chart: { type: 'line', toolbar: { show: false } },
                xaxis: { categories: [] },
                yaxis: [{ title: { text: 'Plnění %' } }],
            };
        }
    }

    get sortedWorkplaceData(): PlanFulfillmentByWorkplace[] {
        return [...this.workplaceData].sort((a, b) => (b.fulfillmentPct ?? 0) - (a.fulfillmentPct ?? 0));
    }

    get workplaceSeries(): any[] {
        if (this.workplaceChartMode === 'trend') {
            // Trend mode: series per workplace
            if (this.workplaceTrendData.length === 0) return [];
            
            const visibleDays = this.workplaceTrendDataVisible.map(d => d.day);
            
            return this.workplaceTrendData.map((wp) => {
                const data: number[] = [];
                for (const day of visibleDays) {
                    const point = wp.points.find(p => p.day === day);
                    data.push(point ? (point.fulfillmentPct ?? 0) : 0);
                }
                
                return {
                    name: wp.workplaceName,
                    type: 'column',
                    data: data,
                };
            });
        } else {
            // Average mode: single horizontal bar series
            try {
                if (!this.sortedWorkplaceData || this.sortedWorkplaceData.length === 0) {
                    console.log('[PlanFulfillment] workplaceSeries: No workplace data available');
                    return [];
                }
                
                const series = [
            {
                name: this.$t('tpmAnalytics.fulfillmentPct'),
                data: this.sortedWorkplaceData.map((w) => ({
                    x: w.workplaceName,
                    y: w.fulfillmentPct ?? 0,
                    fillColor: this.getBarColor(w.fulfillmentPct),
                })),
            },
        ];
                
                console.log('[PlanFulfillment] workplaceSeries generated:', {
                    workplaceCount: this.sortedWorkplaceData.length,
                    seriesCount: series.length,
                    firstWorkplace: this.sortedWorkplaceData[0],
                });
                
                return series;
            } catch (error) {
                console.error('[PlanFulfillment] Error in workplaceSeries:', error, {
                    workplaceDataLength: this.workplaceData?.length,
                });
                return [];
            }
        }
    }

    get workplaceTrendDataVisible(): PlanFulfillmentTrendPoint[] {
        if (this.workplaceChartMode !== 'trend') return [];
        
        // Spojíme všechny points z workplaceTrendData do jednoho pole dnů
        const allDays = new Set<string>();
        for (const wp of this.workplaceTrendData) {
            for (const pt of wp.points) {
                allDays.add(pt.day);
            }
        }
        const sortedDays = Array.from(allDays).sort();
        
        if (this.wpViewStart == null || this.wpViewEnd == null) {
            return sortedDays.map(day => ({ day, fulfillmentPct: null, pieceDiff: 0, planCount: 0, okCount: 0 }));
        }
        
        const start = Math.max(0, Math.min(sortedDays.length - 1, this.wpViewStart));
        const end = Math.max(start, Math.min(sortedDays.length - 1, this.wpViewEnd));
        return sortedDays.slice(start, end + 1).map(day => ({ day, fulfillmentPct: null, pieceDiff: 0, planCount: 0, okCount: 0 }));
    }

    get workplaceChartHeight(): number {
        if (this.workplaceChartMode === 'trend') {
            return 520; // Fixní výška pro trend režim
        }
        const minBarHeight = 30;
        const maxBarHeight = 60;
        const spacing = 10;
        const baseHeight = 120; // Pro osy, legendu a spacing
        
        // Výpočet skutečné výšky baru
        const calculatedBarHeight = Math.floor(420 / this.sortedWorkplaceData.length) - 10;
        const actualBarHeight = Math.min(maxBarHeight, Math.max(minBarHeight, calculatedBarHeight));
        
        // Celková výška = počet barů * (výška baru + spacing) + základní výška
        const calculatedHeight = this.sortedWorkplaceData.length * (actualBarHeight + spacing) + baseHeight;
        return Math.max(300, calculatedHeight);
    }

    get workplaceOptions(): any {
        const self = this;
        const dayStartMinutes = getDayStartMinutes();
        const dayStartMs = dayStartMinutes * 60 * 1000;
        
        if (this.workplaceChartMode === 'trend') {
            // Trend mode: X = time periods, Y = Fulfillment % for each workplace
            const categories = this.workplaceTrendDataVisible.map((d) => {
                const m = moment(d.day);
                if (this.granularity === 'day') {
                    return m.format('D.M.');
                } else if (this.granularity === 'week') {
                    const start = m.clone().startOf('isoWeek');
                    const end = m.clone().endOf('isoWeek');
                    const week = m.isoWeek();
                    const weekLabel = `${week}. týden`;
                    const dateRange = `(${start.format('DD.MM.')} - ${end.format('DD.MM.')})`;
                    return weekLabel + '|' + dateRange;
                } else {
                    return m.format('MM/YYYY');
                }
            });
            
            const shouldRotate = this.granularity === 'week' || categories.length > 8;
            
            return {
                chart: {
                    id: 'fulfillment-workplace-trend',
                    type: 'bar',
                    stacked: false,
                    toolbar: { show: false },
                    zoom: { enabled: false },
                    animations: { enabled: false },
                },
                plotOptions: {
                    bar: {
                        columnWidth: '80%',
                        dataLabels: {
                            position: 'top',
                        },
                    },
                },
                xaxis: {
                    categories,
                    labels: {
                        rotate: shouldRotate ? -45 : 0,
                        rotateAlways: shouldRotate,
                        hideOverlappingLabels: true,
                        trim: true,
                        minHeight: shouldRotate ? 60 : undefined,
                        maxHeight: shouldRotate ? 80 : undefined,
                        style: {
                            cssClass: 'apexcharts-xaxis-label-multiline',
                        },
                        formatter(value: any) {
                            if (typeof value === 'string' && value.includes('|')) {
                                const parts = value.split('|');
                                return parts;
                            }
                            return value;
                        },
                    },
                },
                yaxis: {
                    title: { text: 'Plnění %' },
                    labels: {
                        formatter: (val: number) => val ? val.toFixed(0) + '%' : '0%',
                    },
                },
                dataLabels: { enabled: false },
                legend: { position: 'top' },
                tooltip: {
                    shared: true,
                    intersect: false,
                    followCursor: true,
                    y: {
                        formatter: (val: number) => val != null ? val.toFixed(1) + '%' : '—',
                    },
                },
                colors: ['#0284c7', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'],
                annotations: {
                    yaxis: [{
                        y: 100,
                        borderColor: '#10b981',
                        strokeDashArray: 4,
                    }],
                },
            } as any;
        } else {
            // Average mode: horizontal bar chart
            const calculatedBarHeight = Math.floor(this.workplaceChartHeight / this.sortedWorkplaceData.length) - 10;
            const barHeight = Math.min(60, Math.max(30, calculatedBarHeight)); // Max 60px, min 30px
        return {
            chart: { type: 'bar', toolbar: { show: false }, zoom: { enabled: false } },
            plotOptions: {
                bar: {
                    horizontal: true,
                    distributed: true,
                    dataLabels: { position: 'top' },
                    barHeight: barHeight + 'px',
                },
            },
            xaxis: { categories: this.sortedWorkplaceData.map((w) => w.workplaceName) },
            yaxis: { title: { text: 'Plnění %' } },
            dataLabels: {
                enabled: true,
                formatter: (val: number) => val ? val.toFixed(1) + '%' : '0%',
                style: {
                    colors: ['#fff'],
                    fontSize: '12px',
                    fontWeight: 'bold',
                },
                offsetX: -30,
            },
            legend: { show: false },
            tooltip: { 
                shared: true, 
                intersect: false,
                followCursor: true,
                y: {
                    formatter: (val: number) => val ? val.toFixed(1) + '%' : '0%',
                },
            },
        };
        }
    }

    get projectTrendDataVisible(): any[] {
        // Spojíme všechny points z projectData do jednoho pole dnů/týdnů
        const allDays = new Set<string>();
        for (const project of this.projectData) {
            if (!project.points || !Array.isArray(project.points)) continue;
            for (const pt of project.points) {
                if (pt && pt.day) allDays.add(pt.day);
            }
        }
        const sortedDays = Array.from(allDays).sort();
        
        if (this.projViewStart == null || this.projViewEnd == null) {
            return sortedDays.map(day => ({ day, fulfillmentPct: 0 }));
        }
        
        const start = Math.max(0, Math.min(sortedDays.length - 1, this.projViewStart));
        const end = Math.max(start, Math.min(sortedDays.length - 1, this.projViewEnd));
        return sortedDays.slice(start, end + 1).map(day => ({ day, fulfillmentPct: 0 }));
    }

    get projectSeries(): any[] {
        try {
            if (!this.projectData || this.projectData.length === 0) {
                return [];
            }
        
            const visibleDays = this.projectTrendDataVisible.map(d => d.day);
            
            if (visibleDays.length === 0) {
                return [];
            }
        
            // Pro každý projekt vytvoříme normalizované pole dat
            const series = this.projectData.map((proj) => {
                const dayMap = new Map(proj.points?.map(p => [p.day, p.fulfillmentPct ?? 0]) || []);
            
                return {
                    name: proj.projectName,
                    data: visibleDays.map(day => dayMap.get(day) ?? 0),
                };
            });
            
            return series;
        } catch (error) {
            console.error('[PlanFulfillment] Error in projectSeries:', error);
            return [];
        }
    }

    get projectOptions(): any {
        try {
        const self = this;
        const dayStartMinutes = getDayStartMinutes();
        const dayStartMs = dayStartMinutes * 60 * 1000;
        
        const categories: string[] = [];
        const visiblePoints = this.projectTrendDataVisible;
        
        for (const point of visiblePoints) {
            let timestamp: number;
            
            if (this.granularity === 'week') {
                timestamp = moment(point.day).startOf('isoWeek').valueOf() + dayStartMs;
            } else if (this.granularity === 'month') {
                timestamp = moment(point.day).startOf('month').valueOf() + dayStartMs;
            } else {
                timestamp = moment(point.day).startOf('day').valueOf() + dayStartMs;
            }
            
            categories.push(self.formatAxisLabel(timestamp, self.granularity));
        }
            
            console.log('[PlanFulfillment] projectOptions generated:', {
                categoriesCount: categories.length,
                granularity: this.granularity,
            });
        
        return {
            chart: { id: 'fulfillment-by-project', type: 'line', toolbar: { show: false }, zoom: { enabled: false } },
            stroke: { width: 2, curve: 'smooth' },
            xaxis: { 
                type: 'category',
                categories: categories,
                labels: {
                    rotate: self.granularity === 'week' ? -45 : 0,
                    formatter(value: any) {
                        if (typeof value === 'string' && value.includes('|')) {
                            const parts = value.split('|');
                            return parts;
                        }
                        return value;
                    },
                },
            },
            yaxis: {
                title: { text: 'Plnění %' },
                labels: { formatter: (val: number) => val ? val.toFixed(0) + '%' : '0%' },
            },
            legend: { position: 'top' },
            dataLabels: { enabled: false },
            tooltip: { 
                shared: true, 
                intersect: false, 
                followCursor: true,
                y: {
                    formatter: (val: number) => val != null ? val.toFixed(1) + '%' : '—',
                },
            },
            markers: {
                size: 4,
                hover: {
                    size: 6,
                },
            },
            annotations: {
                yaxis: [{
                    y: 100,
                    borderColor: '#10b981',
                    strokeDashArray: 4,
                }],
            },
        };
        } catch (error) {
            console.error('[PlanFulfillment] Error in projectOptions:', error);
            return {
                chart: { id: 'fulfillment-by-project', type: 'line', toolbar: { show: false } },
                xaxis: { categories: [] },
                yaxis: { title: { text: 'Plnění %' } },
            };
        }
    }

    get teamTrendDataVisible(): any[] {
        const allDays = new Set<string>();
        for (const team of this.teamData) {
            if (!team.points || !Array.isArray(team.points)) continue;
            for (const pt of team.points) {
                if (pt && pt.day) allDays.add(pt.day);
            }
        }
        const sortedDays = Array.from(allDays).sort();
        
        if (this.teamViewStart == null || this.teamViewEnd == null) {
            return sortedDays.map(day => ({ day, fulfillmentPct: 0 }));
        }
        
        const start = Math.max(0, Math.min(sortedDays.length - 1, this.teamViewStart));
        const end = Math.max(start, Math.min(sortedDays.length - 1, this.teamViewEnd));
        return sortedDays.slice(start, end + 1).map(day => ({ day, fulfillmentPct: 0 }));
    }

    get teamSeries(): any[] {
        try {
            if (!this.teamData || this.teamData.length === 0) {
                return [];
            }
        
            const visibleDays = this.teamTrendDataVisible.map(d => d.day);
            
            if (visibleDays.length === 0) {
                return [];
            }
        
            const series = this.teamData.map((team) => {
                const dayMap = new Map(team.points?.map(p => [p.day, p.fulfillmentPct ?? 0]) || []);
            
                return {
                    name: team.shiftTeamTypeName,
                    data: visibleDays.map(day => dayMap.get(day) ?? 0),
                };
            });
            
            return series;
        } catch (error) {
            console.error('[PlanFulfillment] Error in teamSeries:', error);
            return [];
        }
    }

    get teamOptions(): any {
        const self = this;
        const dayStartMinutes = getDayStartMinutes();
        const dayStartMs = dayStartMinutes * 60 * 1000;
        
        const categories: string[] = [];
        const visiblePoints = this.teamTrendDataVisible;
        
        for (const point of visiblePoints) {
            let timestamp: number;
            
            if (this.granularity === 'week') {
                timestamp = moment(point.day).startOf('isoWeek').valueOf() + dayStartMs;
            } else if (this.granularity === 'month') {
                timestamp = moment(point.day).startOf('month').valueOf() + dayStartMs;
            } else {
                timestamp = moment(point.day).startOf('day').valueOf() + dayStartMs;
            }
            
            categories.push(self.formatAxisLabel(timestamp, self.granularity));
        }
        
        return {
            chart: { id: 'fulfillment-by-team', type: 'line', toolbar: { show: false }, zoom: { enabled: false } },
            stroke: { width: 2, curve: 'smooth' },
            xaxis: { 
                type: 'category',
                categories: categories,
                labels: {
                    rotate: self.granularity === 'week' ? -45 : 0,
                    formatter(value: any) {
                        if (typeof value === 'string' && value.includes('|')) {
                            const parts = value.split('|');
                            return parts;
                        }
                        return value;
                    },
                },
            },
            yaxis: {
                title: { text: 'Plnění %' },
                labels: { formatter: (val: number) => val ? val.toFixed(0) + '%' : '0%' },
            },
            legend: { position: 'top' },
            dataLabels: { enabled: false },
            tooltip: { 
                shared: true, 
                intersect: false, 
                followCursor: true,
                y: {
                    formatter: (val: number) => val != null ? val.toFixed(1) + '%' : '—',
                },
            },
            markers: {
                size: 4,
                hover: {
                    size: 6,
                },
            },
            annotations: {
                yaxis: [{
                    y: 100,
                    borderColor: '#10b981',
                    strokeDashArray: 4,
                }],
            },
        };
    }

    get typeTrendDataVisible(): any[] {
        const allDays = new Set<string>();
        for (const type of this.typeData) {
            if (!type.points || !Array.isArray(type.points)) continue;
            for (const pt of type.points) {
                if (pt && pt.day) allDays.add(pt.day);
            }
        }
        const sortedDays = Array.from(allDays).sort();
        
        if (this.typeViewStart == null || this.typeViewEnd == null) {
            return sortedDays.map(day => ({ day, fulfillmentPct: 0 }));
        }
        
        const start = Math.max(0, Math.min(sortedDays.length - 1, this.typeViewStart));
        const end = Math.max(start, Math.min(sortedDays.length - 1, this.typeViewEnd));
        return sortedDays.slice(start, end + 1).map(day => ({ day, fulfillmentPct: 0 }));
    }

    get typeSeries(): any[] {
        try {
            if (!this.typeData || this.typeData.length === 0) {
                return [];
            }
        
            const visibleDays = this.typeTrendDataVisible.map(d => d.day);
            
            if (visibleDays.length === 0) {
                return [];
            }
        
            const series = this.typeData.map((type) => {
                const dayMap = new Map(type.points?.map(p => [p.day, p.fulfillmentPct ?? 0]) || []);
            
                return {
                    name: type.shiftTypeName,
                    data: visibleDays.map(day => dayMap.get(day) ?? 0),
                };
            });
            
            return series;
        } catch (error) {
            console.error('[PlanFulfillment] Error in typeSeries:', error);
            return [];
        }
    }

    get typeOptions(): any {
        const self = this;
        const dayStartMinutes = getDayStartMinutes();
        const dayStartMs = dayStartMinutes * 60 * 1000;
        
        const categories: string[] = [];
        const visiblePoints = this.typeTrendDataVisible;
        
        for (const point of visiblePoints) {
            let timestamp: number;
            
            if (this.granularity === 'week') {
                timestamp = moment(point.day).startOf('isoWeek').valueOf() + dayStartMs;
            } else if (this.granularity === 'month') {
                timestamp = moment(point.day).startOf('month').valueOf() + dayStartMs;
            } else {
                timestamp = moment(point.day).startOf('day').valueOf() + dayStartMs;
            }
            
            categories.push(self.formatAxisLabel(timestamp, self.granularity));
        }
        
        return {
            chart: { id: 'fulfillment-by-type', type: 'line', toolbar: { show: false }, zoom: { enabled: false } },
            stroke: { width: 2, curve: 'smooth' },
            xaxis: { 
                type: 'category',
                categories: categories,
                labels: {
                    rotate: self.granularity === 'week' ? -45 : 0,
                    formatter(value: any) {
                        if (typeof value === 'string' && value.includes('|')) {
                            const parts = value.split('|');
                            return parts;
                        }
                        return value;
                    },
                },
            },
            yaxis: {
                title: { text: 'Plnění %' },
                labels: { formatter: (val: number) => val ? val.toFixed(0) + '%' : '0%' },
            },
            legend: { position: 'top' },
            dataLabels: { enabled: false },
            tooltip: { 
                shared: true, 
                intersect: false, 
                followCursor: true,
                y: {
                    formatter: (val: number) => val != null ? val.toFixed(1) + '%' : '—',
                },
            },
            markers: {
                size: 4,
                hover: {
                    size: 6,
                },
            },
            annotations: {
                yaxis: [{
                    y: 100,
                    borderColor: '#10b981',
                    strokeDashArray: 4,
                }],
            },
        };
    }

    get distributionSeries(): any[] {
        try {
            if (!this.distributionData) {
                console.log('[PlanFulfillment] distributionSeries: No distribution data available');
                return [];
            }
            
            if (!this.distributionData.ranges || !Array.isArray(this.distributionData.ranges)) {
                console.warn('[PlanFulfillment] distributionSeries: Invalid ranges in distribution data');
                return [];
            }
        
        // Spočítáme celkový počet směn
        const totalShifts = this.distributionData.ranges.reduce((sum, r) => sum + r.count, 0);
        
            const series = [
            {
                name: this.$t('tpmAnalytics.percentage'),
                data: this.distributionData.ranges.map((r) => ({
                    x: r.label,
                    y: totalShifts > 0 ? (r.count / totalShifts) * 100 : 0,
                    count: r.count, // Uchováme počet pro tooltip
                })),
            },
        ];
            
            console.log('[PlanFulfillment] distributionSeries generated:', {
                rangesCount: this.distributionData.ranges.length,
                totalShifts: totalShifts,
                seriesCount: series.length,
            });
            
            return series;
        } catch (error) {
            console.error('[PlanFulfillment] Error in distributionSeries:', error, {
                distributionData: this.distributionData,
            });
            return [];
        }
    }

    get distributionOptions(): any {
        const self = this;
        // Spočítáme celkový počet směn pro tooltip
        const totalShifts = this.distributionData?.ranges.reduce((sum, r) => sum + r.count, 0) || 0;
        
        return {
            chart: { type: 'bar', toolbar: { show: false }, zoom: { enabled: false } },
            plotOptions: { bar: { distributed: true } },
            colors: ['#ef4444', '#f59e0b', '#eab308', '#84cc16', '#10b981'],
            xaxis: {
                categories: this.distributionData?.ranges.map((r) => r.label) || [],
            },
            yaxis: { 
                title: { text: 'Podíl směn (%)' },
                labels: { formatter: (val: number) => val.toFixed(1) + '%' },
                max: 100,
            },
            dataLabels: { 
                enabled: true,
                formatter: (val: number) => val.toFixed(1) + '%',
            },
            legend: { show: false },
            tooltip: { 
                shared: true, 
                intersect: false,
                followCursor: true,
                y: {
                    formatter: function(val: number, opts: any) {
                        const count = self.distributionData?.ranges[opts.dataPointIndex]?.count || 0;
                        return `${val.toFixed(1)}% (${count} směn)`;
                    },
                },
            },
        };
    }

    get topHeaders(): any[] {
        return [
            { text: this.$t('tpmAnalytics.workplace'), value: 'workplaceName' },
            { text: this.$t('tpmAnalytics.date'), value: 'date' },
            { text: this.$t('tpmAnalytics.plan'), value: 'planCount' },
            { text: this.$t('tpmAnalytics.actual'), value: 'okCount' },
            { text: this.$t('tpmAnalytics.fulfillment'), value: 'fulfillmentPct' },
        ];
    }

    formatPct(value: number | null | undefined): string {
        if (value == null) return '—';
        return value.toFixed(1) + '%';
    }

    formatPieces(value: number): string {
        return Math.round(value).toLocaleString();
    }

    formatPieceDiff(value: number): string {
        const sign = value > 0 ? '+' : '';
        return sign + this.formatPieces(value);
    }

    formatDelta(pct: number): string {
        const sign = pct > 0 ? '+' : '';
        return `${sign}${pct.toFixed(1)}%`;
    }

    getFulfillmentClass(pct: number | null): string {
        if (pct == null) return '';
        if (pct >= 100) return 'text-green';
        if (pct >= 90) return 'text-yellow';
        return 'text-red';
    }

    getBarColor(pct: number | null): string {
        if (pct == null) return '#9ca3af';
        if (pct >= 100) return '#10b981';
        if (pct >= 90) return '#eab308';
        return '#ef4444';
    }

    formatAxisLabel(ts: number, gran: 'day' | 'week' | 'month', forTooltip = false): string {
        const m = moment(ts);
        if (!m.isValid()) return '';
        if (gran === 'month') return m.format('MM/YYYY');
        if (gran === 'week') {
            const start = m.clone().startOf('isoWeek');
            const end = m.clone().endOf('isoWeek');
            const week = m.isoWeek();
            const weekLabel = `${week}. týden`;
            const dateRange = `(${start.format('DD.MM.')} - ${end.format('DD.MM.')})`;
            
            if (forTooltip) {
                return `${weekLabel}<br/>${dateRange}`;
            }
            return weekLabel + '|' + dateRange;
        }
        return m.format('D.M.');
    }

    getCurrentFilterState(): any {
        return {
            periodPreset: this.periodPreset,
            customFrom: this.customFrom,
            customTo: this.customTo,
            selectedLocationIds: this.selectedLocationIds,
            selectedWorkplaceIds: this.selectedWorkplaceIds,
            selectedProjectIds: this.selectedProjectIds,
            selectedShiftTypeIds: this.selectedShiftTypeIds,
            selectedShiftTeamTypeIds: this.selectedShiftTeamTypeIds,
            granularity: this.granularity,
            workplaceChartMode: this.workplaceChartMode,
        };
    }

    applyFilterState(state: any, triggerLoad = true): void {
        console.log('[PlanFulfillment] applyFilterState called:', {
            triggerLoad,
            applyingInitial: this.applyingInitial,
            state: { ...state },
        });
        
        this.periodPreset = state.periodPreset || 'last7d';
        this.customFrom = state.customFrom || null;
        this.customTo = state.customTo || null;
        this.selectedLocationIds = state.selectedLocationIds || [];
        this.selectedWorkplaceIds = state.selectedWorkplaceIds || [];
        this.selectedProjectIds = state.selectedProjectIds || [];
        this.selectedShiftTypeIds = state.selectedShiftTypeIds || [];
        this.selectedShiftTeamTypeIds = state.selectedShiftTeamTypeIds || [];
        this.granularity = state.granularity || 'day';
        this.workplaceChartMode = state.workplaceChartMode || 'trend';
        
        if (triggerLoad) {
            console.log('[PlanFulfillment] Calling onFiltersChanged()');
            this.onFiltersChanged();
        } else {
            console.log('[PlanFulfillment] Skipping load (triggerLoad=false)');
        }
    }

    onFiltersChanged(): void {
        console.log('[PlanFulfillment] onFiltersChanged called:', {
            applyingInitial: this.applyingInitial,
        });
        
        if (this.applyingInitial) {
            console.log('[PlanFulfillment] Skipping onFiltersChanged (applyingInitial=true)');
            return;
        }
        
        // Debounce 200ms - zabráníme duplicitnímu volání při rychlé změně více filtrů
        if (this.filtersDebounceTimer) clearTimeout(this.filtersDebounceTimer);
        this.filtersDebounceTimer = setTimeout(() => {
            console.log('[PlanFulfillment] Debounce timeout - calling load()');
        this.load();
            const fn = (this.$root as any).$tpmOnStateChanged;
            if (typeof fn === 'function') fn();
        }, 200);
    }

    async load(): Promise<void> {
        // Zabránění paralelním načítáním
        if (this.loading) {
            console.log('[PlanFulfillment] load(): Already loading, skipping');
            return;
        }
        
        this.loading = true;
        console.log('[PlanFulfillment] load(): Starting data load');
        
        try {
        const range = getDateRange({
            periodPreset: this.periodPreset as any,
            customFrom: this.customFrom,
            customTo: this.customTo,
        });
        const filters = {
            locationIds: this.selectedLocationIds,
            workplaceIds: this.selectedWorkplaceIds,
            projectIds: this.selectedProjectIds,
            shiftTypeIds: this.selectedShiftTypeIds,
            shiftTeamTypeIds: this.selectedShiftTeamTypeIds,
        };

            console.log('[PlanFulfillment] load(): Fetching analytics with params:', {
                range: { from: range.from, to: range.to },
                granularity: this.granularity,
                filters,
            });

            // OPTIMALIZACE: Pouze 1 volání místo 8 - načte směny jen 2x (current + previous)
            const analytics = await getAllPlanFulfillmentAnalytics(range.from, range.to, this.granularity, filters);

            console.log('[PlanFulfillment] load(): Analytics received:', {
                trendPoints: analytics.trend?.length || 0,
                workplaces: analytics.byWorkplace?.length || 0,
                projects: analytics.byProject?.length || 0,
                teams: analytics.byShiftTeam?.length || 0,
                types: analytics.byShiftType?.length || 0,
                hasDistribution: !!analytics.distribution,
                bestItems: analytics.topItems?.best?.length || 0,
                worstItems: analytics.topItems?.worst?.length || 0,
            });

            // Rozbalení výsledků
            this.fulfillmentPct = analytics.summary.fulfillmentPct;
            this.avgDailyFulfillmentPct = analytics.summary.avgDailyFulfillmentPct;
            this.daysUnderPlan = analytics.summary.daysUnderPlan;
            this.totalDays = analytics.summary.totalDays;
            this.totalPieceDiff = analytics.summary.totalPieceDiff;
            this.prevFulfillmentPct = analytics.summary.prev?.fulfillmentPct ?? null;
            this.prevAvgDailyFulfillmentPct = analytics.summary.prev?.avgDailyFulfillmentPct ?? null;

            this.trend = analytics.trend || [];
            this.workplaceData = analytics.byWorkplace || [];
            this.workplaceTrendData = analytics.workplaceTrend || [];
            this.projectData = analytics.byProject || [];
            this.teamData = analytics.byShiftTeam || [];
            this.typeData = analytics.byShiftType || [];
            this.distributionData = analytics.distribution || null;
            this.bestItems = analytics.topItems?.best || [];
            this.worstItems = analytics.topItems?.worst || [];

            console.log('[PlanFulfillment] load(): Data assigned, calling initZoom()');
        this.initZoom();
            console.log('[PlanFulfillment] load(): Completed successfully');
        } catch (error) {
            console.error('[PlanFulfillment] Error loading data:', error);
            // Zobrazíme detailnější informace o chybě
            if (error instanceof Error) {
                console.error('[PlanFulfillment] Error details:', {
                    message: error.message,
                    stack: error.stack,
                });
            }
        } finally {
            this.loading = false;
        }
    }

    initZoom(): void {
        try {
        const optimal = getOptimalWindowSize(this.granularity);
        this.viewStart = Math.max(0, this.trend.length - optimal);
        this.viewEnd = this.trend.length;
        
        // Workplace trend zoom
        const allDays = new Set<string>();
        for (const wp of this.workplaceTrendData) {
            for (const pt of wp.points) {
                allDays.add(pt.day);
            }
        }
        const wpLen = allDays.size;
        this.wpViewStart = Math.max(0, wpLen - optimal);
        this.wpViewEnd = wpLen;
        
        this.projViewStart = 0;
            this.projViewEnd = this.projectData[0]?.points?.length || 10;
        
        this.teamViewStart = 0;
            this.teamViewEnd = this.teamData[0]?.points?.length || 10;
        
        this.typeViewStart = 0;
            this.typeViewEnd = this.typeData[0]?.points?.length || 10;
            
            console.log('[PlanFulfillment] initZoom completed:', {
                granularity: this.granularity,
                optimal: optimal,
                trendView: `${this.viewStart} - ${this.viewEnd} (total: ${this.trend.length})`,
                projectView: `${this.projViewStart} - ${this.projViewEnd}`,
                teamView: `${this.teamViewStart} - ${this.teamViewEnd}`,
                typeView: `${this.typeViewStart} - ${this.typeViewEnd}`,
            });
        } catch (error) {
            console.error('[PlanFulfillment] Error in initZoom:', error);
            // Nastavíme výchozí hodnoty
            this.viewStart = 0;
            this.viewEnd = 10;
            this.projViewStart = 0;
            this.projViewEnd = 10;
            this.teamViewStart = 0;
            this.teamViewEnd = 10;
            this.typeViewStart = 0;
            this.typeViewEnd = 10;
        }
    }

    getVisibleData<T>(data: T[], start: number | null, end: number | null): T[] {
        try {
            if (!data || !Array.isArray(data)) {
                console.warn('[PlanFulfillment] getVisibleData: Invalid data array');
                return [];
            }
        if (start == null || end == null) return data;
            
            // Validace bounds
            const validStart = Math.max(0, Math.min(start, data.length));
            const validEnd = Math.max(validStart, Math.min(end, data.length));
            
            return data.slice(validStart, validEnd);
        } catch (error) {
            console.error('[PlanFulfillment] Error in getVisibleData:', error, {
                dataLength: data?.length,
                start,
                end,
            });
            return [];
        }
    }

    zoomIn(): void {
        if (this.viewStart == null || this.viewEnd == null) return;
        const range = this.viewEnd - this.viewStart;
        const newRange = Math.max(7, Math.round(range * 0.75));
        const mid = Math.floor((this.viewStart + this.viewEnd) / 2);
        this.viewStart = Math.max(0, mid - Math.floor(newRange / 2));
        this.viewEnd = Math.min(this.trend.length, this.viewStart + newRange);
    }

    zoomOut(): void {
        if (this.viewStart == null || this.viewEnd == null) return;
        const range = this.viewEnd - this.viewStart;
        const newRange = Math.min(this.trend.length, Math.round(range * 1.25));
        const mid = Math.floor((this.viewStart + this.viewEnd) / 2);
        this.viewStart = Math.max(0, mid - Math.floor(newRange / 2));
        this.viewEnd = Math.min(this.trend.length, this.viewStart + newRange);
    }

    shiftPercent(pct: number): void {
        if (this.viewStart == null || this.viewEnd == null) return;
        const range = this.viewEnd - this.viewStart;
        const shift = Math.round(range * pct);
        this.viewStart = Math.max(0, Math.min(this.trend.length - range, this.viewStart + shift));
        this.viewEnd = this.viewStart + range;
    }

    wpZoomIn(): void {
        if (this.wpViewStart == null || this.wpViewEnd == null) return;
        const allDays = new Set<string>();
        for (const wp of this.workplaceTrendData) {
            for (const pt of wp.points) {
                allDays.add(pt.day);
            }
        }
        const maxLen = allDays.size;
        const range = this.wpViewEnd - this.wpViewStart;
        const newRange = Math.max(7, Math.round(range * 0.75));
        const mid = Math.floor((this.wpViewStart + this.wpViewEnd) / 2);
        this.wpViewStart = Math.max(0, mid - Math.floor(newRange / 2));
        this.wpViewEnd = Math.min(maxLen, this.wpViewStart + newRange);
    }

    wpZoomOut(): void {
        if (this.wpViewStart == null || this.wpViewEnd == null) return;
        const allDays = new Set<string>();
        for (const wp of this.workplaceTrendData) {
            for (const pt of wp.points) {
                allDays.add(pt.day);
            }
        }
        const maxLen = allDays.size;
        const range = this.wpViewEnd - this.wpViewStart;
        const newRange = Math.min(maxLen, Math.round(range * 1.5));
        const mid = Math.floor((this.wpViewStart + this.wpViewEnd) / 2);
        this.wpViewStart = Math.max(0, mid - Math.floor(newRange / 2));
        this.wpViewEnd = Math.min(maxLen, this.wpViewStart + newRange);
    }

    wpShiftPercent(pct: number): void {
        if (this.wpViewStart == null || this.wpViewEnd == null) return;
        const allDays = new Set<string>();
        for (const wp of this.workplaceTrendData) {
            for (const pt of wp.points) {
                allDays.add(pt.day);
            }
        }
        const maxLen = allDays.size;
        const range = this.wpViewEnd - this.wpViewStart;
        const shift = Math.round(range * pct);
        this.wpViewStart = Math.max(0, Math.min(maxLen - range, this.wpViewStart + shift));
        this.wpViewEnd = this.wpViewStart + range;
    }

    projZoomIn(): void {
        if (this.projViewStart == null || this.projViewEnd == null) return;
        const range = this.projViewEnd - this.projViewStart;
        const newRange = Math.max(7, Math.round(range * 0.75));
        
        const allDays = new Set<string>();
        for (const project of this.projectData) {
            if (!project.points) continue;
            for (const pt of project.points) {
                if (pt && pt.day) allDays.add(pt.day);
            }
        }
        const maxLen = allDays.size;
        
        const mid = Math.floor((this.projViewStart + this.projViewEnd) / 2);
        this.projViewStart = Math.max(0, mid - Math.floor(newRange / 2));
        this.projViewEnd = Math.min(maxLen - 1, this.projViewStart + newRange - 1);
    }

    projZoomOut(): void {
        if (this.projViewStart == null || this.projViewEnd == null) return;
        const range = this.projViewEnd - this.projViewStart;
        
        const allDays = new Set<string>();
        for (const project of this.projectData) {
            if (!project.points) continue;
            for (const pt of project.points) {
                if (pt && pt.day) allDays.add(pt.day);
            }
        }
        const maxLen = allDays.size;
        
        const newRange = Math.min(maxLen, Math.round(range * 1.25));
        const mid = Math.floor((this.projViewStart + this.projViewEnd) / 2);
        this.projViewStart = Math.max(0, mid - Math.floor(newRange / 2));
        this.projViewEnd = Math.min(maxLen - 1, this.projViewStart + newRange - 1);
    }

    projShiftPercent(pct: number): void {
        if (this.projViewStart == null || this.projViewEnd == null) return;
        const range = this.projViewEnd - this.projViewStart + 1;
        
        const allDays = new Set<string>();
        for (const project of this.projectData) {
            if (!project.points) continue;
            for (const pt of project.points) {
                if (pt && pt.day) allDays.add(pt.day);
            }
        }
        const maxLen = allDays.size;
        
        const shift = Math.round(range * pct);
        this.projViewStart = Math.max(0, Math.min(maxLen - range, this.projViewStart + shift));
        this.projViewEnd = this.projViewStart + range - 1;
    }
    
    teamZoomIn(): void {
        if (this.teamViewStart == null || this.teamViewEnd == null) return;
        const range = this.teamViewEnd - this.teamViewStart;
        const newRange = Math.max(7, Math.round(range * 0.75));
        
        const allDays = new Set<string>();
        for (const team of this.teamData) {
            if (!team.points) continue;
            for (const pt of team.points) {
                if (pt && pt.day) allDays.add(pt.day);
            }
        }
        const maxLen = allDays.size;
        
        const mid = Math.floor((this.teamViewStart + this.teamViewEnd) / 2);
        this.teamViewStart = Math.max(0, mid - Math.floor(newRange / 2));
        this.teamViewEnd = Math.min(maxLen - 1, this.teamViewStart + newRange - 1);
    }

    teamZoomOut(): void {
        if (this.teamViewStart == null || this.teamViewEnd == null) return;
        const range = this.teamViewEnd - this.teamViewStart;
        
        const allDays = new Set<string>();
        for (const team of this.teamData) {
            if (!team.points) continue;
            for (const pt of team.points) {
                if (pt && pt.day) allDays.add(pt.day);
            }
        }
        const maxLen = allDays.size;
        
        const newRange = Math.min(maxLen, Math.round(range * 1.25));
        const mid = Math.floor((this.teamViewStart + this.teamViewEnd) / 2);
        this.teamViewStart = Math.max(0, mid - Math.floor(newRange / 2));
        this.teamViewEnd = Math.min(maxLen - 1, this.teamViewStart + newRange - 1);
    }

    teamShiftPercent(pct: number): void {
        if (this.teamViewStart == null || this.teamViewEnd == null) return;
        const range = this.teamViewEnd - this.teamViewStart + 1;
        
        const allDays = new Set<string>();
        for (const team of this.teamData) {
            if (!team.points) continue;
            for (const pt of team.points) {
                if (pt && pt.day) allDays.add(pt.day);
            }
        }
        const maxLen = allDays.size;
        
        const shift = Math.round(range * pct);
        this.teamViewStart = Math.max(0, Math.min(maxLen - range, this.teamViewStart + shift));
        this.teamViewEnd = this.teamViewStart + range - 1;
    }
    
    typeZoomIn(): void {
        if (this.typeViewStart == null || this.typeViewEnd == null) return;
        const range = this.typeViewEnd - this.typeViewStart;
        const newRange = Math.max(7, Math.round(range * 0.75));
        
        const allDays = new Set<string>();
        for (const type of this.typeData) {
            if (!type.points) continue;
            for (const pt of type.points) {
                if (pt && pt.day) allDays.add(pt.day);
            }
        }
        const maxLen = allDays.size;
        
        const mid = Math.floor((this.typeViewStart + this.typeViewEnd) / 2);
        this.typeViewStart = Math.max(0, mid - Math.floor(newRange / 2));
        this.typeViewEnd = Math.min(maxLen - 1, this.typeViewStart + newRange - 1);
    }

    typeZoomOut(): void {
        if (this.typeViewStart == null || this.typeViewEnd == null) return;
        const range = this.typeViewEnd - this.typeViewStart;
        
        const allDays = new Set<string>();
        for (const type of this.typeData) {
            if (!type.points) continue;
            for (const pt of type.points) {
                if (pt && pt.day) allDays.add(pt.day);
            }
        }
        const maxLen = allDays.size;
        
        const newRange = Math.min(maxLen, Math.round(range * 1.25));
        const mid = Math.floor((this.typeViewStart + this.typeViewEnd) / 2);
        this.typeViewStart = Math.max(0, mid - Math.floor(newRange / 2));
        this.typeViewEnd = Math.min(maxLen - 1, this.typeViewStart + newRange - 1);
    }

    typeShiftPercent(pct: number): void {
        if (this.typeViewStart == null || this.typeViewEnd == null) return;
        const range = this.typeViewEnd - this.typeViewStart + 1;
        
        const allDays = new Set<string>();
        for (const type of this.typeData) {
            if (!type.points) continue;
            for (const pt of type.points) {
                if (pt && pt.day) allDays.add(pt.day);
            }
        }
        const maxLen = allDays.size;
        
        const shift = Math.round(range * pct);
        this.typeViewStart = Math.max(0, Math.min(maxLen - range, this.typeViewStart + shift));
        this.typeViewEnd = this.typeViewStart + range - 1;
    }

    downloadChart(name: string): void {
        console.log(`Download chart: ${name}`);
    }

    $watch!: any;

    created(): void {
        console.log('[PlanFulfillment] created() called');
        // Registrace rozhraní pro parent a načtení stavu z aktuálně vybraného filtru
        (this.$root as any).$tpmGetCurrentState = this.getCurrentFilterState.bind(this);
        (this.$root as any).$tpmApplyState = this.applyFilterState.bind(this);
        
        this.applyingInitial = true;
        
        const initState = (this.$root as any).$tpmInitialState || {};
        const initSid = (this.$root as any).$tpmInitialSid || null;
        
        console.log('[PlanFulfillment] created() - initState:', initState, 'initSid:', initSid);
        
        if (initSid) {
            console.log('[PlanFulfillment] Loading filter from initSid:', initSid);
            getFilterById(initSid).then((model) => {
                console.log('[PlanFulfillment] Filter loaded:', model);
                if (model) {
                    const st = unpackSettings(model.settings);
                    this.applyFilterState(st, false);
                }
                this.applyingInitial = false;
                // Po načtení filtru explicitně zavoláme load
                this.onFiltersChanged();
            }).catch((err) => {
                console.error('[PlanFulfillment] Error loading filter:', err);
                this.applyingInitial = false;
                this.onFiltersChanged();
            });
        } else if (Object.keys(initState).length > 0) {
            console.log('[PlanFulfillment] Applying initState');
            this.applyFilterState(initState, false);
            this.applyingInitial = false;
        } else {
            console.log('[PlanFulfillment] No initial state - mounted() will call load()');
            // Žádný initial state - mounted() zavolá load()
            this.applyingInitial = false;
        }
    }
    
    mounted(): void {
        console.log('[PlanFulfillment] mounted() called, applyingInitial:', this.applyingInitial);
        // V mounted() voláme load() pouze pokud už není applyingInitial
        // (pokud je, created() ho zavolá až se načte filtr)
        if (!this.applyingInitial) {
            console.log('[PlanFulfillment] mounted() - calling onFiltersChanged()');
            this.onFiltersChanged();
        } else {
            console.log('[PlanFulfillment] mounted() - skipping onFiltersChanged (applyingInitial=true)');
        }
        
        // Watch na granularitu - když se změní, znovu načteme data
        this.$watch('granularity', () => {
            this.onFiltersChanged();
        });
        
        this.$watch('workplaceChartMode', () => {
            this.onFiltersChanged();
            // Update parent state for URL persistence and tab synchronization
            const currentState = this.getCurrentFilterState();
            this.$parent?.$emit('update-state', currentState);
        });
    }

    destroyed(): void {
        // Cleanup debounce timer
        if (this.filtersDebounceTimer) {
            clearTimeout(this.filtersDebounceTimer);
        }
        
        if ((this.$root as any).$tpmGetCurrentState === this.getCurrentFilterState) {
            delete (this.$root as any).$tpmGetCurrentState;
        }
        if ((this.$root as any).$tpmApplyState === this.applyFilterState) {
            delete (this.$root as any).$tpmApplyState;
        }
    }
}
